import React, { useState, useEffect } from 'react';

const DestinationForm = ({ onSubmit, onCancel, editingDestination = null }) => {
  const [formData, setFormData] = useState({
    type: 'webhook',
    config: {},
    active: true,
  });
  const [slackConnectionType, setSlackConnectionType] = useState('webhook');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Initialize form if editing an existing destination
  useEffect(() => {
    if (editingDestination) {
      setIsEditing(true);
      setFormData({
        ...editingDestination,
        _id: editingDestination._id // Keep the ID for updating
      });
      
      // Set slack connection type based on config
      if (editingDestination.type === 'slack') {
        setSlackConnectionType(editingDestination.config.webhookUrl ? 'webhook' : 'channel');
      }
    } else {
      setIsEditing(false);
    }
  }, [editingDestination]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }

    // Reset config when destination type changes
    if (name === 'type') {
      setFormData({
        ...formData,
        type: value,
        config: {},
      });
    }
  };

  const handleConfigChange = (key, value) => {
    setFormData({
      ...formData,
      config: { ...formData.config, [key]: value },
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    try {
      // Validation based on destination type
      if (formData.type === 'webhook' && !formData.config.url) {
        setError('Webhook URL is required');
        setLoading(false);
        return;
      }

      if (formData.type === 'slack') {
        // Ensure either webhook URL or channel name is provided for Slack
        if (!formData.config.webhookUrl && !formData.config.channelName) {
          setError('Either Slack webhook URL or channel name is required');
          setLoading(false);
          return;
        }
      }

      if (formData.type === 'googleSheets') {
        if (!formData.config.spreadsheetId) {
          setError('Google Sheets Spreadsheet ID is required');
          setLoading(false);
          return;
        }
        
        if (formData.config.insertMode === 'upsert' && !formData.config.matchFields) {
          setError('Match fields are required when using update mode');
          setLoading(false);
          return;
        }
      }

      onSubmit(formData);
    } catch (err) {
      setError('An error occurred while adding the destination');
    } finally {
      setLoading(false);
    }
  };

  const renderTypeSpecificFields = () => {
    switch (formData.type) {
      case 'webhook':
        return (
          <>
            <div className="mb-4">
              <label htmlFor="url" className="label">
                Webhook URL
              </label>
              <input
                id="url"
                type="url"
                value={formData.config.url || ''}
                onChange={(e) => handleConfigChange('url', e.target.value)}
                className="input"
                placeholder="https://example.com/webhook"
                required
              />
            </div>
            <div className="mb-4">
              <label htmlFor="method" className="label">
                HTTP Method
              </label>
              <select
                id="method"
                value={formData.config.method || 'POST'}
                onChange={(e) => handleConfigChange('method', e.target.value)}
                className="input"
              >
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
              </select>
            </div>
            <div className="mb-4">
              <label htmlFor="headers" className="label">
                Headers (JSON format, optional)
              </label>
              <textarea
                id="headers"
                value={
                  formData.config.headers
                    ? JSON.stringify(formData.config.headers, null, 2)
                    : ''
                }
                onChange={(e) => {
                  try {
                    const headers = e.target.value
                      ? JSON.parse(e.target.value)
                      : {};
                    handleConfigChange('headers', headers);
                  } catch (err) {
                    // Allow partial input while typing
                    handleConfigChange('headers_raw', e.target.value);
                  }
                }}
                className="input font-mono text-sm"
                placeholder='{ "Content-Type": "application/json", "Authorization": "Bearer token" }'
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter headers as a JSON object
              </p>
            </div>
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.config.sendAsJson !== false}
                  onChange={(e) => handleConfigChange('sendAsJson', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mr-2"
                />
                <span className="text-sm text-gray-900">Send data as JSON</span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                When unchecked, data will be sent as a string
              </p>
            </div>
          </>
        );

      case 'slack':
        return (
          <>
            <div className="mb-4">
              <label className="label">Connection Method</label>
              <div className="flex flex-col gap-2 mt-2">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="slackConnectionType"
                    value="webhook"
                    checked={slackConnectionType === 'webhook'}
                    onChange={() => {
                      setSlackConnectionType('webhook');
                      // Clear channel name if webhook is selected
                      const newConfig = { ...formData.config };
                      delete newConfig.channelName;
                      setFormData({
                        ...formData,
                        config: newConfig,
                      });
                    }}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Slack Webhook</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="slackConnectionType"
                    value="channel"
                    checked={slackConnectionType === 'channel'}
                    onChange={() => {
                      setSlackConnectionType('channel');
                      // Clear webhook URL if channel is selected
                      const newConfig = { ...formData.config };
                      delete newConfig.webhookUrl;
                      setFormData({
                        ...formData,
                        config: {
                          ...newConfig,
                          channelName: '',
                        },
                      });
                    }}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Slack Channel</span>
                </label>
              </div>
            </div>

            {slackConnectionType === 'webhook' ? (
              <div className="mb-4">
                <label htmlFor="webhookUrl" className="label">
                  Slack Webhook URL
                </label>
                <input
                  id="webhookUrl"
                  type="url"
                  value={formData.config.webhookUrl || ''}
                  onChange={(e) => handleConfigChange('webhookUrl', e.target.value)}
                  className="input"
                  placeholder="https://hooks.slack.com/services/..."
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  <a
                    href="https://api.slack.com/messaging/webhooks"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline"
                  >
                    Learn how to create a Slack webhook
                  </a>
                </p>
              </div>
            ) : (
              <div className="mb-4">
                <label htmlFor="channelName" className="label">
                  Slack Channel Name
                </label>
                <input
                  id="channelName"
                  type="text"
                  value={formData.config.channelName || ''}
                  onChange={(e) => handleConfigChange('channelName', e.target.value)}
                  className="input"
                  placeholder="#general"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Channel name where messages will be sent (e.g., #general)
                </p>
                <p className="text-xs text-blue-600 mt-2">
                  Using Slack Bot Token from environment variables
                </p>
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="message" className="label">
                Custom Message (optional)
              </label>
              <input
                id="message"
                type="text"
                value={formData.config.message || ''}
                onChange={(e) => handleConfigChange('message', e.target.value)}
                className="input"
                placeholder="Data enriched via DataFunnel"
              />
            </div>
          </>
        );

      case 'googleSheets':
        return (
          <>
            <div className="mb-4">
              <label htmlFor="spreadsheetId" className="label">
                Spreadsheet ID
              </label>
              <input
                id="spreadsheetId"
                type="text"
                value={formData.config.spreadsheetId || ''}
                onChange={(e) => handleConfigChange('spreadsheetId', e.target.value)}
                className="input"
                placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Find this in your Google Sheets URL:
                docs.google.com/spreadsheets/d/
                <span className="font-medium">SPREADSHEET_ID</span>/edit
              </p>
            </div>
            <div className="mb-4">
              <label htmlFor="sheetName" className="label">
                Sheet Name (optional)
              </label>
              <input
                id="sheetName"
                type="text"
                value={formData.config.sheetName || ''}
                onChange={(e) => handleConfigChange('sheetName', e.target.value)}
                className="input"
                placeholder="Sheet1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave blank to use the first sheet
              </p>
            </div>
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.config.flattenObjects === true}
                  onChange={(e) => handleConfigChange('flattenObjects', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mr-2"
                />
                <span className="text-sm text-gray-900">Flatten Object fields</span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                When enabled, nested object fields will be flattened into separate columns.
                Otherwise, objects will be stored as JSON strings.
              </p>
            </div>
            <div className="mb-4">
              <label htmlFor="insertMode" className="label">
                Insert Mode
              </label>
              <select
                id="insertMode"
                value={formData.config.insertMode || 'append'}
                onChange={(e) => handleConfigChange('insertMode', e.target.value)}
                className="input"
              >
                <option value="append">Always add new rows</option>
                <option value="upsert">Update existing rows or add new</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Choose whether to always add new rows or update existing rows when matches are found.
              </p>
            </div>
            {formData.config.insertMode === 'upsert' && (
              <div className="mb-4">
                <label htmlFor="matchFields" className="label">
                  Match Fields
                </label>
                <input
                  id="matchFields"
                  type="text"
                  value={formData.config.matchFields || ''}
                  onChange={(e) => handleConfigChange('matchFields', e.target.value)}
                  className="input"
                  placeholder="email, id, name"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Comma-separated list of field names to match existing rows. 
                  If a row with matching values is found, it will be updated; otherwise, a new row will be added.
                </p>
              </div>
            )}
            <div className="bg-yellow-50 p-4 rounded-md mb-4">
              <p className="text-yellow-700 text-sm">
                Google Sheets integration requieres that you add adbrew@adbrew.iam.gserviceaccount.com as an editor to your Google Sheet.
              </p>  
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 p-3 rounded-md">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      <div className="mb-4">
        <label htmlFor="type" className="label">
          Destination Type
        </label>
        <select
          id="type"
          name="type"
          value={formData.type}
          onChange={handleChange}
          className="input"
          required
          disabled={isEditing} // Cannot change destination type when editing
        >
          <option value="webhook">Webhook</option>
          <option value="slack">Slack</option>
          <option value="googleSheets">Google Sheets</option>
        </select>
        {isEditing && (
          <p className="text-xs text-gray-500 mt-1">
            Destination type cannot be changed when editing
          </p>
        )}
      </div>

      {renderTypeSpecificFields()}

      <div className="flex items-center mb-4">
        <input
          id="active"
          name="active"
          type="checkbox"
          checked={formData.active}
          onChange={handleChange}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
        <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
          Active (send data to this destination)
        </label>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading}
        >
          {loading ? (isEditing ? 'Updating...' : 'Adding...') : (isEditing ? 'Update Destination' : 'Add Destination')}
        </button>
      </div>
    </form>
  );
};

export default DestinationForm;